/**
 * One piece, every colour.
 *
 * Many colours of a piece are separate products with the same name. This file says which
 * products are colours of one piece ("siblings"), what each colour is called, and shows
 * them: on a product card as colour dots that work like a product's own colours, and on the
 * product page as links beside its own colours (printed by snippets/sibling-colors.liquid;
 * this file names them and carries the chosen size across).
 *
 * WHO ARE SIBLINGS. Decided by the system, from the product index
 * (templates/collection.kz-index.liquid): products with the same title (spaces at the ends
 * and capital letters aside), the same fit label and the same category, for the same
 * gender. The title is the one the page shows: the index is in the page's language, as the
 * product page is, so both always agree with each other. (Two products whose names differ
 * in English and are the same in Arabic are one piece on Arabic pages.) A person can overrule it for one product with the product metafield
 * `custom.siblings` (a list of products): the list then replaces what the system found for
 * that product. The metafield is never needed.
 *
 * WHAT A COLOUR IS CALLED, in order: the product's only Colour option value; else the end of
 * its title after " - " or "–" when that names a colour ("Linen Shirt - Navy"); else its
 * product type when that names a colour; else the colour read from its first photo, by the
 * same rules that paint the dot (assets/swatch-color.js). Nothing else is ever guessed: a
 * product type such as "T-Shirt" is not a colour and is never shown as one.
 *
 * The first part of this file has no page code: the tests (scripts/siblings.test.mjs) and
 * the list for review (scripts/sibling-groups.mjs) read it too.
 */
import { familyOf, toHsv } from './swatch-color.js';

/** The words a colour read from a photo can be given. The page shows them in its language. */
export const WORDS = { black: 'Black', white: 'White', gray: 'Gray', charcoal: 'Charcoal', beige: 'Beige', navy: 'Navy Blue', blue: 'Blue', green: 'Green', khaki: 'Khaki', bordo: 'Bordo', red: 'Red', pink: 'Pink', purple: 'Purple', orange: 'Orange', yellow: 'Yellow', brown: 'Brown', camel: 'Camel' };

const between = (h, from, to) => (from <= to ? h >= from && h <= to : h >= from || h <= to);

/**
 * The plain name of a shade: a key of WORDS, or '' when it has none.
 * Broad on purpose: a photo's light shifts a shade, and "Beige" is honest for cream, sand and
 * off-white alike. Dark first, then the greys and whites, then the pale warm shades, then by hue.
 * @param {string} hex - '#rrggbb'
 */
export function familyNameOf(hex) {
  if (!/^#[0-9a-f]{6}$/i.test(hex || '')) return '';
  const [h, s, v] = toHsv([1, 3, 5].map((at) => parseInt(hex.slice(at, at + 2), 16)));
  if (v < 0.22 || (v < 0.3 && s < 0.3)) return 'black';
  if (s < 0.06) return v > 0.8 ? 'white' : v < 0.45 ? 'charcoal' : 'gray';
  // A pale yellow is yellow, not cream.
  if (between(h, 0.1, 0.19) && s >= 0.17 && v > 0.9) return 'yellow';
  if (between(h, 0.98, 0.2) && s < 0.4 && v > 0.52) return 'beige';
  if (s < 0.2) return v < 0.45 ? 'charcoal' : 'gray';
  if (between(h, 0.55, 0.75) && v < 0.45) return 'navy';
  if (between(h, 0.86, 0.04) && v < 0.65) return 'bordo';
  if (between(h, 0.93, 0.04) && s > 0.45) return 'red';
  if (between(h, 0.82, 0.02) && v > 0.6) return 'pink';
  if (between(h, 0.7, 0.86)) return 'purple';
  if (between(h, 0.02, 0.1) && s > 0.65 && v > 0.45) return 'orange';
  if (between(h, 0.05, 0.115) && s > 0.3 && v > 0.45) return 'camel';
  if (between(h, 0.97, 0.13) && v < 0.62) return 'brown';
  if (between(h, 0.1, 0.19) && s > 0.35 && v > 0.6) return 'yellow';
  if (between(h, 0.08, 0.3) && v < 0.75) return 'khaki';
  if (between(h, 0.2, 0.5)) return 'green';
  if (between(h, 0.5, 0.72)) return 'blue';
  return '';
}

/** The end of a title after " - " or "–", when it names a colour: "Linen Shirt - Navy" gives "Navy". */
export function nameFromTitle(title) {
  const parts = String(title || '').split(/\s+-\s+|\s*–\s*/);
  const last = parts.length > 1 ? parts[parts.length - 1].trim() : '';
  return last && familyOf(last) ? last : '';
}

/**
 * What a product's colour is called by its own data. The photo is the page's to read.
 * @param {object} p - A record of the product index
 * @returns {{ name: string, shown: string, from: 'option'|'title'|'type'|'' }}
 */
export function namedColour(p) {
  if (p.cv?.length === 1) return { name: p.cv[0][0], shown: p.cv[0][5] || p.cv[0][0], from: 'option' };
  const fromTitle = nameFromTitle(p.t);
  if (fromTitle) return { name: fromTitle, shown: fromTitle, from: 'title' };
  if (p.ty && familyOf(p.ty)) return { name: p.ty, shown: p.ty, from: 'type' };
  return { name: '', shown: '', from: '' };
}

/** What makes two products one piece: title, fit label, category and gender. */
export const pieceKey = (p) => [String(p.t || '').trim().toLowerCase().replace(/\s+/g, ' '), p.fr || '', p.c || '', p.g || ''].join('|');

/**
 * Every product's siblings.
 * @param {{ list: object[], by: Record<string, object> }} index - What kz-recs.js `build` returns
 * @returns {Map<string, string[]>} handle → the handles of its piece, itself first, then by best-seller rank. Only products that have at least one sibling.
 */
export function siblings(index) {
  const pieces = new Map();
  for (const p of index.list) {
    const key = pieceKey(p);
    if (!pieces.has(key)) pieces.set(key, []);
    pieces.get(key).push(p);
  }
  const result = new Map();
  for (const p of index.list) {
    // A list kept by a person replaces the system's, for the product it is saved on.
    const others = Array.isArray(p.sb) ? p.sb.map((h) => index.by[h]).filter((other) => other && other.h !== p.h) : pieces.get(pieceKey(p)).filter((other) => other.h !== p.h);
    if (!others.length) continue;
    result.set(p.h, [p.h, ...[...new Set(others)].sort((a, b) => a.rank - b.rank).map((other) => other.h)]);
  }
  return result;
}

// ─── The page part ────────────────────────────────────────────────────────────

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const KS = window.KanzeSiblings;
  const words = KS?.words || {};
  /** A colour's plain name in the page's language, from the shade a dot was painted with. */
  const shownName = (hex) => words[familyNameOf(hex)] || '';
  const sized = (address, width) => String(address || '').replace(/([?&]width=)\d+/, `$1${width}`);

  /* ---------- Product cards: the groups, kept on the device ---------- */

  /** What a card needs to know about one colour: [handle, address, photo, name, in stock, words for the photo's rules, shade, price as a card prints it, saving]. */
  const entry = (p, old) => {
    const named = namedColour(p);
    return [p.h, p.url, p.img ? sized(p.img, 96) : '', named.shown || old?.[3] || '', p.av ? 1 : 0, `${p.ty} ${p.t}`, old?.[6] || '', p.ph || '', p.sv || ''];
  };

  async function refresh(tries = 0) {
    const config = JSON.parse(document.querySelector('[data-kz-config]')?.textContent || 'null');
    if (!KS || !config) return;
    if (KS.data?.stamp === config.stamp) return;
    // The script that holds the index (assets/kz-modules.js) may still be on its way.
    if (!window.kz?.index) {
      if (tries < 20) window.setTimeout(() => refresh(tries + 1).catch(() => {}), 500);
      return;
    }
    const index = await window.kz.index();
    // The index may only just have been read and built: the rest waits its own turn, so no
    // one stretch of work holds the page.
    await new Promise((resolve) => window.setTimeout(resolve, 0));
    // What earlier visits worked out from the photos is kept: a photo is read once.
    const before = {};
    for (const group of Object.values(KS.data?.by || {})) for (const item of group) before[item[0]] = item;
    const by = {};
    for (const [handle, group] of siblings(index)) by[handle] = group.map((h) => entry(index.by[h], before[h]));
    KS.save({ stamp: config.stamp, by });
    await new Promise((resolve) => window.setTimeout(resolve, 0));
    // First time on this device: a card whose end he can already see is left as it is (its
    // dots would push what is under it). The ones that end below the screen get theirs now.
    KS.apply(document, true);
  }

  if (KS) {
    // Cards that arrive later ("Show more", another category, a row of suggestions): their
    // dots are added before the browser draws them.
    new MutationObserver((changes) => {
      for (const change of changes) {
        for (const node of change.addedNodes) {
          if (node instanceof Element && (node.matches('.card, [data-handle]') || node.querySelector('.card'))) KS.apply(node, false);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
    const idle = window.requestIdleCallback ? (work) => window.requestIdleCallback(work, { timeout: 4000 }) : (work) => window.setTimeout(work, 1200);
    idle(() => refresh().catch(() => {}));
  }

  /**
   * A dot took its shade from its photo (now, or on an earlier visit): the colour gets its
   * name, and the shade is kept for the cards. A shade that only stands in for a photo not
   * read yet names nothing.
   */
  const painted = (dot) => {
    const holder = dot.closest('[data-sibling]');
    const hex = (dot.dataset.colours || '').split(',')[0];
    const source = dot.dataset.source || '';
    if (!holder || !hex || !/^(photo|remembered)/.test(source)) return;
    const named = holder.dataset.siblingNamed === 'true';
    const name = named ? '' : shownName(hex);
    if (name) {
      const label = holder.querySelector('[data-sibling-name]');
      if (label) label.textContent = name;
      if (holder.matches('a, span')) holder.title = name;
      // The colour being looked at: its name stands after "Colour:".
      if (holder.hasAttribute('data-sibling-current')) {
        const line = holder.closest('.picker__option')?.querySelector('[data-sibling-current-name]');
        if (line) line.textContent = name;
      }
    }
    if (!KS?.data) return;
    let changed = false;
    for (const group of Object.values(KS.data.by)) {
      for (const item of group) {
        if (item[0] !== holder.dataset.sibling || (item[6] === hex && (named || !name || item[3] === name))) continue;
        item[6] = hex;
        if (!named && name) item[3] = name;
        changed = true;
      }
    }
    if (changed) KS.save(KS.data);
  };
  document.addEventListener('swatch:paint', (event) => event.target instanceof Element && painted(event.target));
  // The dots painted before this script was listening (a shade remembered from an earlier visit).
  for (const dot of document.querySelectorAll('[data-sibling] swatch-dot[data-colours]')) painted(dot);

  /* ---------- The product page: the chosen size goes with him to another colour ---------- */

  const norm = (text) => String(text || '').trim().toLowerCase();
  const carrySize = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-sibling][data-sizes]') : null;
    if (!link) return;
    const chosen = link.closest('[data-product-root]')?.querySelector('[data-size-option] input:checked');
    const address = new URL(link.dataset.url || link.href, window.location.origin);
    link.dataset.url = link.dataset.url || link.getAttribute('href');
    // The sizes that colour has in stock, each with its variant: "S=123|M=456|".
    const sizes = (link.dataset.sizes || '').split('|').filter(Boolean).map((pair) => [pair.slice(0, pair.lastIndexOf('=')), pair.slice(pair.lastIndexOf('=') + 1)]);
    // Only a size that colour has, and has in stock: its own variant is asked for by number,
    // so the page arrives with the size already chosen.
    const variant = chosen ? sizes.find(([size]) => norm(size) === norm(chosen.value))?.[1] : null;
    if (variant) address.searchParams.set('variant', variant);
    link.href = address.pathname + address.search;
  };
  // Before the browser follows the link, whichever way it is pressed.
  for (const sign of ['pointerdown', 'keydown', 'focusin']) document.addEventListener(sign, carrySize, { passive: true });
}
